import { Extension, StateField } from "@codemirror/state";
import { Decoration, DecorationSet, EditorView } from "@uiw/react-codemirror";

/// Where a runtime error happened, as character offsets into the source the
/// server was given.
export interface RuntimeErrorRange {
    start: number;
    end: number;
}

const theme = EditorView.baseTheme({
    ".cm-runtimeError": {
        backgroundColor: "rgba(255, 82, 82, 0.25)",
        borderBottom: "2px solid #ff5252",
    },
});

const mark = Decoration.mark({ class: "cm-runtimeError" });

/// Highlights the expression a runtime error came from.
///
/// The decoration is built once from `error` rather than tracked through
/// document changes, because an edit invalidates the error itself -- the editor
/// clears it on the next change. It is built from the editor's own document, so
/// the extension depends on `error` alone and stays the same between
/// keystrokes.
const runtimeErrorHighlight = (error: RuntimeErrorRange | null): Extension => {
    if (error == null) return [];

    const field = StateField.define<DecorationSet>({
        create: (state) => {
            const docLength = state.doc.length;

            // the error came from a run of source the document may have moved
            // past, so the range is clamped rather than trusted
            const start = Math.min(error.start, docLength);
            const end = Math.min(Math.max(error.end, start + 1), docLength);

            // nothing left to point at
            if (start >= end) return Decoration.none;

            return Decoration.set([mark.range(start, end)]);
        },
        update: (decorations, tr) => decorations.map(tr.changes),
        provide: (f) => EditorView.decorations.from(f),
    });

    return [theme, field];
};

export default runtimeErrorHighlight;
